import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { parseDurationToMilliseconds } from './utils/DateUtils';
import openNotification from './utils/CustomNotification';
 
// --- Styled Components ---
 
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;
 
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.65);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(4px);
`;
 
const ModalContent = styled.div`
  background: #ffffff;
  padding: 40px 30px;
  border-radius: 20px;
  width: 90%;
  max-width: 420px;
  text-align: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  font-family: "Inter", sans-serif;
  animation: ${fadeIn} 0.3s ease-out;
  border: 1px solid #f0f0f0;
`;
 
const IconWrapper = styled.div<{ $isError?: boolean }>`
  font-size: 40px;
  margin-bottom: 20px;
  color: ${props => props.$isError ? '#ef4444' : '#f59e0b'};
`;
 
const Title = styled.h2`
  margin: 0 0 10px 0;
  color: #1f2937;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;
 
const Message = styled.p`
  color: #6b7280;
  font-size: 15px;
  margin-bottom: 30px;
  line-height: 1.6;
`;
 
const TimeEmphasis = styled.span`
  color: #d946ef; /* Distinct color for timer */
  font-weight: 700;
  font-size: 18px;
  font-variant-numeric: tabular-nums;
`;
 
const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 10px;
`;
 
const Button = styled.button`
  flex: 1;
  padding: 12px 20px;
  border-radius: 12px;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
 
  &.primary {
    background: #aaeff4;
    color: #000;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
 
    &:hover {
      background-color: #92e4ea;
      transform: translateY(-1px);
    }
   
    &:active {
      transform: translateY(0);
    }
  }
 
  &.secondary {
    background: #f3f4f6;
    color: #4b5563;
   
    &:hover {
      background-color: #e5e7eb;
      color: #1f2937;
    }
  }
 
  &.danger {
    background: #ef4444;
    color: white;
    width: 100%; /* Full width for single button */
   
    &:hover {
      background-color: #dc2626;
    }
  }
`;
 
// --- Logic Component ---
 
const EVENTS = ['mousemove', 'keydown', 'click', 'scroll'];
const beAddress = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
 
const SessionTimeoutManager: React.FC = () => {
    const [showModal, setShowModal] = useState(false);
    const [isSessionExpired, setIsSessionExpired] = useState(false); // New State: Timer hit 0
    const [timeLeftDisplay, setTimeLeftDisplay] = useState(0);
    const navigate = useNavigate();
 
    // 1. Get Config
    const sessionTimeoutStr = localStorage.getItem("session_timeout");
    const totalTimeoutMs = parseDurationToMilliseconds(sessionTimeoutStr);
    const warningThresholdMs = totalTimeoutMs * 0.90;
 
    const resetLocalActivity = useCallback(() => {
        localStorage.setItem('lastActiveTime', Date.now().toString());
    }, []);
 
    // ---------------------------------------------------------
    // A. LOGOUT HANDLER (Modified)
    // Calls API first, but ALWAYS redirects even if API fails
    // ---------------------------------------------------------
    const handleLogout = useCallback(async () => {
        try {
            const userUid = localStorage.getItem("user_uid");
 
            // Attempt to tell server we are leaving
            // (Using a generic logout endpoint assumption - adjust URL if needed)
            if (userUid) {
                await axios.post(`${beAddress}/api/logout`, {}, {
                    headers: { "user-uid": userUid },
                    withCredentials: true
                });
            }
        } catch (error) {
            // If API fails (e.g. session already dead), we effectively ignore it
            // because the user needs to be logged out locally anyway.
            console.warn("Logout API call failed or session already expired on server.");
        } finally {
            // ✅Critical: Always clear local state and redirect
            localStorage.clear();
            setShowModal(false);
            setIsSessionExpired(false);
            navigate("/");
        }
    }, [navigate]);
 
    // ---------------------------------------------------------
    // B. MULTI-TAB SYNC
    // ---------------------------------------------------------
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'user_uid' && event.newValue === null) {
                navigate("/");
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [navigate]);
 
    // ---------------------------------------------------------
    // C. ACTIVITY TRACKER (Optimized)
    // ---------------------------------------------------------
    useEffect(() => {
        if (!totalTimeoutMs) return;
 
        // If modal is open (Warning or Expired), STOP tracking mouse.
        if (showModal || isSessionExpired) return;
 
        // Ensure we have a starting value
        if (!localStorage.getItem('lastActiveTime')) {
            resetLocalActivity();
        }
 
        let throttleTimer: NodeJS.Timeout | null = null;
 
        const handleActivity = () => {
            // "Leading Edge" Throttle
            if (!throttleTimer) {
                // 1. Write to storage immediately on first move
                resetLocalActivity();
 
                // 2. Block further writes for 10 seconds (Efficiency Fix)
                // CHANGED FROM 1000 TO 30000
                throttleTimer = setTimeout(() => {
                    throttleTimer = null;
                }, 30000);
            }
        };
 
        EVENTS.forEach(event => window.addEventListener(event, handleActivity));
 
        return () => {
            EVENTS.forEach(event => window.removeEventListener(event, handleActivity));
            if (throttleTimer) clearTimeout(throttleTimer);
        };
    }, [resetLocalActivity, totalTimeoutMs, showModal, isSessionExpired]);
 
    // ---------------------------------------------------------
    // D. THE INTERVAL CHECK
    // ---------------------------------------------------------
    useEffect(() => {
        if (!totalTimeoutMs) return;
 
        const intervalId = setInterval(() => {
            // If session is already marked expired in UI, stop checking
            if (isSessionExpired) {
                clearInterval(intervalId);
                return;
            }
 
            const lastActive = parseInt(localStorage.getItem('lastActiveTime') || '0', 10);
 
            if (!lastActive) {
                clearInterval(intervalId);
                return;
            }
 
            const now = Date.now();
            const timeElapsed = now - lastActive;
            const msRemaining = totalTimeoutMs - timeElapsed;
 
            // 1. Session Expired (Time Up!)
            if (timeElapsed >= totalTimeoutMs) {
                clearInterval(intervalId);
                //  CHANGE: Do not logout immediately. Show "Session Ended" UI.
                setIsSessionExpired(true);
                setShowModal(true);
            }
            // 2. Warning Zone (90% elapsed)
            else if (timeElapsed >= warningThresholdMs) {
                if (!showModal) setShowModal(true);
                setTimeLeftDisplay(Math.ceil(msRemaining / 1000));
            }
            // 3. Safe Zone
            else {
                // Only close if we are in "Warning" mode.
                // If we are "Expired", we stay open until user clicks Logout.
                if (showModal && !isSessionExpired) setShowModal(false);
            }
        }, 1000);
 
        return () => clearInterval(intervalId);
    }, [totalTimeoutMs, warningThresholdMs, showModal, isSessionExpired]);
 
    // ---------------------------------------------------------
    // E. EXTEND SESSION
    // ---------------------------------------------------------
    const handleExtendSession = async () => {
        try {
            const userUid = localStorage.getItem("user_uid");
 
            const response = await axios.get(`${beAddress}/api/validate-session`, {
                headers: { "user-uid": userUid },
                withCredentials: true,
            });
 
            if (response.status === 200 && response.data.status === "Success") {
                resetLocalActivity();
                setShowModal(false);
                openNotification({ type: "success", message: "Session Extended" });
            } else {
                // If extension fails, we treat it as an immediate expiry
                setIsSessionExpired(true);
            }
        } catch (error) {
            console.error("Extension failed", error);
            setIsSessionExpired(true);
        }
    };
 
    // --- Render ---
 
    if (!showModal) return null;
 
    return (
        <ModalOverlay>
            <ModalContent>
                {isSessionExpired ? (
                    // --- VIEW 1: SESSION ENDED ---
                    <>
                        <IconWrapper $isError>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                        </IconWrapper>
                        <Title>Session Ended</Title>
                        <Message>
                            For your security, your session has timed out due to inactivity. Please log in again to continue.
                        </Message>
                        <ButtonGroup>
                            <Button className="danger" onClick={handleLogout}>
                                Logout
                            </Button>
                        </ButtonGroup>
                    </>
                ) : (
                    // --- VIEW 2: WARNING COUNTDOWN ---
                    <>
                        <IconWrapper>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </IconWrapper>
                        <Title>Are you still there?</Title>
                        <Message>
                            Your session will expire in <TimeEmphasis>{timeLeftDisplay}s</TimeEmphasis>.
                            <br />
                            Would you like to stay signed in?
                        </Message>
                        <ButtonGroup>
                            <Button className="secondary" onClick={handleLogout}>
                                Logout
                            </Button>
                            <Button className="primary" onClick={handleExtendSession}>
                                Extend Session
                            </Button>
                        </ButtonGroup>
                    </>
                )}
            </ModalContent>
        </ModalOverlay>
    );
};
 
export default SessionTimeoutManager;
 